import React, { useState, useEffect } from 'react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { TrendingUp, DollarSign, PieChart, Calendar } from 'lucide-react';

const SIPCalculatorPage = () => {
  const [inputs, setInputs] = useState({
    monthlyInvestment: 10000,
    years: 10,
    expectedReturn: 12
  });
  const [result, setResult] = useState(null);

  useEffect(() => {
    calculateSIP();
  }, [inputs]);

  const calculateSIP = () => {
    const P = parseFloat(inputs.monthlyInvestment);
    const r = parseFloat(inputs.expectedReturn) / 12 / 100;
    const n = parseInt(inputs.years) * 12;

    const futureValue = P * (((Math.pow(1 + r, n) - 1) / r) * (1 + r));
    const invested = P * n;
    const returns = futureValue - invested;

    setResult({
      futureValue: Math.round(futureValue),
      invested: Math.round(invested),
      returns: Math.round(returns),
      returnPercent: ((returns / invested) * 100).toFixed(1)
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black">
      <Navbar />
      
      <div className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl mb-6">
              <TrendingUp className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              SIP Calculator
            </h1>
            <p className="text-lg text-white/70 max-w-3xl mx-auto">
              Plan your wealth creation journey with systematic investments
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Input Section */}
            <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/10">
              <h2 className="text-2xl font-bold text-white mb-8">Investment Details</h2>
              
              <div className="space-y-8">
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <label className="text-white font-medium">Monthly Investment</label>
                    <span className="text-blue-400 font-bold text-xl">{formatCurrency(inputs.monthlyInvestment)}</span>
                  </div>
                  <input
                    type="range"
                    min="500"
                    max="100000"
                    step="500"
                    value={inputs.monthlyInvestment}
                    onChange={(e) => setInputs({...inputs, monthlyInvestment: e.target.value})}
                    className="w-full h-3 bg-white/10 rounded-lg appearance-none cursor-pointer slider"
                  />
                  <div className="flex justify-between text-white/40 text-xs mt-2">
                    <span>₹500</span>
                    <span>₹1,00,000</span>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-3">
                    <label className="text-white font-medium">Time Period</label>
                    <span className="text-purple-400 font-bold text-xl">{inputs.years} Years</span>
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="30"
                    step="1"
                    value={inputs.years}
                    onChange={(e) => setInputs({...inputs, years: e.target.value})}
                    className="w-full h-3 bg-white/10 rounded-lg appearance-none cursor-pointer slider"
                  />
                  <div className="flex justify-between text-white/40 text-xs mt-2">
                    <span>1 Year</span>
                    <span>30 Years</span>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-3">
                    <label className="text-white font-medium">Expected Return (p.a.)</label>
                    <span className="text-green-400 font-bold text-xl">{inputs.expectedReturn}%</span>
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="30"
                    step="0.5"
                    value={inputs.expectedReturn}
                    onChange={(e) => setInputs({...inputs, expectedReturn: e.target.value})}
                    className="w-full h-3 bg-white/10 rounded-lg appearance-none cursor-pointer slider"
                  />
                  <div className="flex justify-between text-white/40 text-xs mt-2">
                    <span>1%</span>
                    <span>30%</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Results Section */}
            {result && (
              <div className="space-y-6">
                {/* Future Value Card */}
                <div className="bg-gradient-to-br from-blue-500/20 to-purple-500/20 backdrop-blur-xl rounded-3xl p-8 border border-blue-500/30">
                  <div className="flex items-center gap-3 mb-4">
                    <DollarSign className="w-6 h-6 text-blue-400" />
                    <h3 className="text-white/70 text-sm">Future Value</h3>
                  </div>
                  <p className="text-5xl font-bold text-white mb-2">{formatCurrency(result.futureValue)}</p>
                  <p className="text-green-400 text-sm">+{result.returnPercent}% growth</p>
                </div>

                {/* Breakdown Cards */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10">
                    <div className="flex items-center gap-2 mb-2">
                      <PieChart className="w-4 h-4 text-white/50" />
                      <p className="text-white/50 text-xs">Invested Amount</p>
                    </div>
                    <p className="text-2xl font-bold text-white">{formatCurrency(result.invested)}</p>
                  </div>
                  <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="w-4 h-4 text-green-400" />
                      <p className="text-white/50 text-xs">Wealth Gained</p>
                    </div>
                    <p className="text-2xl font-bold text-green-400">{formatCurrency(result.returns)}</p>
                  </div>
                </div>

                {/* Visual Breakdown */}
                <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10">
                  <p className="text-white/70 text-sm mb-3">Investment Breakdown</p>
                  <div className="h-6 bg-white/10 rounded-full overflow-hidden flex">
                    <div 
                      className="bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-500"
                      style={{width: `${(result.invested / result.futureValue) * 100}%`}}
                    ></div>
                    <div 
                      className="bg-gradient-to-r from-green-500 to-green-600 transition-all duration-500"
                      style={{width: `${(result.returns / result.futureValue) * 100}%`}}
                    ></div>
                  </div>
                  <div className="flex justify-between mt-3 text-xs">
                    <span className="text-blue-400">Invested: {((result.invested / result.futureValue) * 100).toFixed(1)}%</span>
                    <span className="text-green-400">Returns: {((result.returns / result.futureValue) * 100).toFixed(1)}%</span>
                  </div>
                </div>

                {/* Recommendation */}
                <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 backdrop-blur-xl rounded-2xl p-6 border border-amber-500/30">
                  <p className="text-amber-400 font-semibold mb-2">💡 Smart Tip</p>
                  <p className="text-white/80 text-sm">
                    {inputs.expectedReturn >= 12 
                      ? "You're targeting equity-like returns. Consider diversifying across large-cap and mid-cap funds for balanced growth."
                      : "For conservative returns, consider hybrid funds or balanced advantage funds for stability with growth potential."}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <style jsx>{`
        .slider::-webkit-slider-thumb {
          appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: linear-gradient(135deg, #3b82f6, #8b5cf6);
          cursor: pointer;
          box-shadow: 0 0 10px rgba(59, 130, 246, 0.5);
        }
        .slider::-moz-range-thumb {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: linear-gradient(135deg, #3b82f6, #8b5cf6);
          cursor: pointer;
          box-shadow: 0 0 10px rgba(59, 130, 246, 0.5);
          border: none;
        }
      `}</style>

      <Footer />
    </div>
  );
};

export default SIPCalculatorPage;